import React, { createContext, useContext, useEffect, ReactNode, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import authService, { AuthState, User } from '../pages/auth/services';
import { useIdToken } from '../hooks/useIdToken';
import { logger } from '../utils/logger';
import userService from 'services/UserService';
import apiService from 'services/ApiService';

// Create a module-specific logger
const log = logger.getLogger('AuthContext');

// Default AuthState
const defaultAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null
};

// Auth Context Type
interface AuthContextType {
  auth: AuthState;
  login: () => void;
  logout: () => void;
  refreshUserProfile: (id: string) => Promise<void>;
  idToken: string | null;
  refreshIdToken: () => Promise<void>;
}

// Create AuthContext with default values
const AuthContext = createContext<AuthContextType>({
  auth: defaultAuthState,
  login: () => {},
  logout: () => {},
  refreshUserProfile: async () => {},
  idToken: null,
  refreshIdToken: async () => {}
});

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Extract user ID from Auth0 sub claim
 * @param sub Auth0 subject identifier (format: provider|id)
 * @returns Extracted user ID
 */
const extractUserIdFromSub = (sub: string): string => {
  const idParts = sub.split('|');
  return idParts.length > 1 ? idParts[1] : sub;
};

/**
 * Map Auth0 user to application User type
 * @param user Auth0 user object
 * @returns Mapped User object
 */
const mapAuth0UserToAppUser = (user: any): User => ({
  id: user.sub || '',
  email: user.email || '',
  name: user.name || '',
  picture: user.picture,
  sub: user.sub,
  nickname: user.nickname,
  updated_at: user.updated_at,
});

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);
  const [silentAuthChecked, setSilentAuthChecked] = useState(false);
  const { idToken, refreshIdToken } = useIdToken();

  // Use Auth0 hooks
  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently
  } = useAuth0();

  /**
   * Refresh user profile from API
   */
  const refreshUserProfile = useCallback(async (id: string): Promise<void> => {
    try {
      await userService.enrichUserWithAccountProfile(id);
      
      setAuthState(prevState => ({
        ...prevState,
        user: userService.getUser()
      }));
    } catch (err) {
      log.error('Error refreshing user profile:', err);
    }
  }, []);

  /**
   * Handle unauthenticated user state
   */
  const handleUnauthenticatedUser = useCallback((errorMessage: string = '') => {
    // Clear tokens from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('idToken');
    
    // Clear services
    apiService.setAuthorizationHeader(null);
    userService.setUserBasicProfile(null);
    authService.setTokens(null, null);
    authService.setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: errorMessage || 'Not authenticated'
    });

    // Update local state
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: errorMessage || (error ? error.message : null)
    });
  }, [error]);

  /**
   * Handle authenticated user state
   */
  const handleAuthenticatedUser = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = await getAccessTokenSilently();
      
      // Store the access token in localStorage for persistence
      localStorage.setItem('auth_token', token);
      
      // First set the tokens in auth service
      authService.setTokens(token, idToken);
      
      // Then set the authorization header with the access token (not the ID token)
      apiService.setAuthorizationHeader(idToken);
      
      const mappedUser = mapAuth0UserToAppUser(user);
      
      // Update auth state
      authService.setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: mappedUser,
        error: null
      });
      
      // Set user profile
      userService.setUserBasicProfile(mappedUser);
      
      // Update local state
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: mappedUser,
        error: error ? error.message : null
      });
      
      // Add a small delay before fetching the user profile to ensure token is properly set
      setTimeout(async () => {
        // Fetch user's account profile if we have a sub ID
        if (user.sub) {
          const userId = extractUserIdFromSub(user.sub);
          await refreshUserProfile(userId);
        }
      }, 500); // 500ms delay
    } catch (tokenError) {
      log.error('Error getting access token:', tokenError);
      handleUnauthenticatedUser('Failed to get access token');
    }
  }, [user, getAccessTokenSilently, idToken, error, refreshUserProfile, handleUnauthenticatedUser]);

  // Try silent authentication when Auth0 SDK finishes loading but user is not authenticated
  // This checks if there's an existing Auth0 session (via cookies) without showing the Auth0 dialog
  useEffect(() => {
    const trySilentAuth = async () => {
      // Only try if Auth0 finished loading, user is not authenticated, and we haven't checked yet
      if (!isLoading && !isAuthenticated && !silentAuthChecked) {
        log.log('Attempting silent authentication...');
        try {
          // This will succeed if there's a valid Auth0 session cookie
          await getAccessTokenSilently();
          log.log('Silent authentication successful');
          // Auth0 SDK will update isAuthenticated automatically
        } catch (err: any) {
          // Silent auth failed - this is expected if no session exists
          log.log('Silent authentication failed (no existing session):', err?.message || err);
        } finally {
          setSilentAuthChecked(true);
        }
      }
    };

    trySilentAuth();
  }, [isLoading, isAuthenticated, silentAuthChecked, getAccessTokenSilently]);

  // Update auth state when Auth0 state changes
  useEffect(() => {
    const updateAuthState = async () => {
      if (isAuthenticated && user) {
        await handleAuthenticatedUser();
      } else if (silentAuthChecked) {
        // Only mark as unauthenticated after silent auth check is complete
        handleUnauthenticatedUser();
      }
    };

    updateAuthState();
  }, [isAuthenticated, user, isLoading, silentAuthChecked, handleAuthenticatedUser, handleUnauthenticatedUser]);

  // Note: We intentionally do NOT auto-redirect to Auth0 here.
  // Instead, ProtectedRoute handles unauthenticated users by redirecting to /login,
  // where the user can click "Log in with Auth0". This provides a better UX and
  // avoids issues with third-party cookie blocking that can cause silent auth to fail.

  /**
   * Login function using Auth0's loginWithRedirect
   */
  const login = useCallback(() => {
    const returnTo = sessionStorage.getItem('returnTo') || '/accounts';
    loginWithRedirect({
      appState: { returnTo }
    });
  }, [loginWithRedirect]);

  /**
   * Logout function using Auth0's logout
   */
  const logout = useCallback(() => {
    auth0Logout({ 
      logoutParams: {
        returnTo: window.location.origin 
      }
    });
  }, [auth0Logout]);

  // Compute effective auth state - handle race conditions between Auth0 and local state
  const effectiveAuthState: AuthState = {
    ...authState,
    // Consider authenticated if EITHER Auth0 says so OR our local state says so
    // This prevents race conditions during silent auth
    isAuthenticated: isAuthenticated || authState.isAuthenticated,
    // Keep loading true while Auth0 is loading OR while silent auth hasn't been checked yet
    isLoading: isLoading || (!isAuthenticated && !silentAuthChecked)
  };

  // Context value
  const contextValue: AuthContextType = {
    auth: effectiveAuthState,
    login,
    logout,
    refreshUserProfile,
    idToken,
    refreshIdToken
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using the auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;